/**
 * WrapShootDialog — day-level "Wrap Shoot" action.
 *
 * Archives every active shoot day for a project WITHOUT flipping
 * project.status. Status remains `active` until an admin clicks the
 * project-header **Complete Project** button (which runs wrap-then-
 * complete atomically). Gated by the Gear check-out count via the
 * shared `enforce_gear_gate` helper on the backend — same 503/409
 * detail shape as the delete + complete paths.
 */
import { useEffect, useState } from "react";
import { PackageCheck, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { projectsApi, callSheetApi } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export function WrapShootDialog({ open, onOpenChange, projectId, onWrapped }) {
  const [phase, setPhase] = useState("checking"); // checking | blocked | confirm | working
  const [gate, setGate] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setPhase("checking");
    setGate(null);
    (async () => {
      try {
        const status = await projectsApi.gearCheckoutStatus(projectId);
        if (cancelled) return;
        setGate(status);
        if (!status.gear_reachable || (status.checked_out_count || 0) > 0) {
          setPhase("blocked");
        } else {
          setPhase("confirm");
        }
      } catch (e) {
        if (cancelled) return;
        setGate({
          checked_out_count: 0,
          gear_reachable: false,
          error: e?.response?.data?.detail || e?.message || "Gear preflight failed",
        });
        setPhase("blocked");
      }
    })();
    return () => { cancelled = true; };
  }, [open, projectId]);

  const handleConfirm = async () => {
    setPhase("working");
    try {
      const res = await callSheetApi.wrapAll(projectId);
      toast.success(
        res.count > 0
          ? `Wrapped ${res.count} shoot day${res.count === 1 ? "" : "s"}`
          : "Shoot wrapped",
      );
      onWrapped?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "gear_checked_out" || detail?.code === "gear_unreachable") {
        setGate({
          checked_out_count: detail.checked_out_count || 0,
          gear_reachable: detail.code !== "gear_unreachable",
          error: detail.gear_error || "",
          message: detail.message,
        });
        setPhase("blocked");
      } else {
        toast.error(detail?.message || detail || "Failed to wrap shoot");
        setPhase("confirm");
      }
    }
  };

  const blocked = phase === "blocked";
  const checkedOut = gate?.checked_out_count || 0;
  const gearUnreachable = gate && gate.gear_reachable === false;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="wrap-shoot-dialog"
        className="bg-zinc-950 border-zinc-800 text-zinc-100 sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-light flex items-center gap-2">
            <PackageCheck className="w-5 h-5 text-emerald-400" />
            Wrap Shoot
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Archive every remaining shoot day. Project stays{" "}
            <span className="text-zinc-300">Active</span> — click{" "}
            <span className="text-emerald-300">Complete Project</span> in the header
            when the whole project is done.
          </DialogDescription>
        </DialogHeader>

        {phase === "checking" && (
          <div
            data-testid="wrap-gear-checking"
            className="flex items-center gap-3 rounded-sm border border-zinc-800 bg-zinc-900/40 px-4 py-4 text-sm text-zinc-400"
          >
            <Loader2 className="w-4 h-4 animate-spin text-amber-400" />
            Checking Gear check-out status…
          </div>
        )}

        {blocked && (
          <div
            data-testid="wrap-gear-blocked-banner"
            className="rounded-sm border-2 border-rose-600 bg-rose-950/40 px-4 py-4 space-y-2"
          >
            <div className="flex items-center gap-2 text-rose-300 font-semibold">
              <AlertTriangle className="w-5 h-5" />
              Action Blocked
            </div>
            {gearUnreachable ? (
              <p className="text-sm text-rose-100/90 leading-relaxed">
                Cannot wrap: the Gear app is unreachable. This action requires
                a confirmed check-out count for data integrity. Try again once
                Gear is back online.
              </p>
            ) : (
              <p className="text-sm text-rose-100/90 leading-relaxed">
                <span className="font-semibold">Action Blocked: Gear is still checked out.</span>{" "}
                Please return{" "}
                <span
                  data-testid="wrap-gear-count"
                  className="font-mono text-rose-200 bg-rose-900/60 px-1.5 py-0.5 rounded"
                >
                  {checkedOut}
                </span>{" "}
                item{checkedOut === 1 ? "" : "s"} in the Gear App first.
              </p>
            )}
            {gate?.error ? (
              <p className="text-[11px] text-rose-300/70 font-mono break-all">
                {gate.error}
              </p>
            ) : null}
          </div>
        )}

        {(phase === "confirm" || phase === "working") && (
          <div
            data-testid="wrap-confirm-body"
            className="rounded-sm border border-zinc-800 bg-zinc-900/40 px-4 py-4 text-sm text-zinc-300 leading-relaxed space-y-2"
          >
            <p>
              Every active shoot day will be archived and its Shoot Day task
              marked complete. Past PDFs and share links keep working under{" "}
              <strong className="text-zinc-100">Past Call Sheets</strong>.
            </p>
          </div>
        )}

        <DialogFooter className="pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={phase === "working"}
            className="bg-transparent border-zinc-800 text-zinc-300 hover:bg-zinc-900 hover:text-zinc-100"
          >
            {blocked ? "Close" : "Cancel"}
          </Button>
          {!blocked && phase !== "checking" && (
            <Button
              type="button"
              data-testid="wrap-shoot-confirm-btn"
              onClick={handleConfirm}
              disabled={phase === "working"}
              className="bg-emerald-600 hover:bg-emerald-500 text-white gap-2"
            >
              {phase === "working" ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Wrapping…
                </>
              ) : (
                <>
                  <PackageCheck className="w-4 h-4" /> Wrap Shoot
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
